/**
 * Sidebar Navigation (SCR-000)
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-000 Application Shell - Sidebar section
 * @iso27001 A.9.4.1 Information access restriction - Role-based nav visibility
 */

import { Link } from 'react-router-dom';
import { Icon, type IconName } from '@myvault/shared/components';
import { cn } from '@myvault/shared/utils';
import type { UserPersona } from '@myvault/shared/types';

interface NavItem {
  id: string;
  icon: IconName;
  label: string;
  href: string;
  matchPrefix?: boolean;
  persona?: UserPersona;
  badge?: number;
}

const mainNavItems: NavItem[] = [
  { id: 'dashboard', icon: 'home', label: 'Dashboard', href: '/dashboard' },
  { id: 'documents', icon: 'folder', label: 'Documenten', href: '/documents', matchPrefix: true },
  { id: 'prompt-builder', icon: 'sparkles', label: 'Prompt Builder', href: '/prompt-builder/templates', matchPrefix: true },
];

const consumerNavItems: NavItem[] = [
  { id: 'delegates', icon: 'userGroup', label: 'Gemachtigden', href: '/delegates', matchPrefix: true, persona: 'retail_consumer' },
];

const delegateNavItems: NavItem[] = [
  { id: 'shared-vaults', icon: 'share', label: 'Gedeelde Kluizen', href: '/gemachtigde/vaults', matchPrefix: true, persona: 'delegate' },
];

const professionalNavItems: NavItem[] = [
  { id: 'professional-dashboard', icon: 'chartBar', label: 'Dashboard', href: '/professional', persona: 'professional_accountant' },
  { id: 'professional-clients', icon: 'buildingOffice', label: 'Cliënten', href: '/professional/clients', matchPrefix: true, persona: 'professional_accountant' },
  { id: 'professional-tasks', icon: 'clipboardDocumentList', label: 'Taken', href: '/professional/tasks', matchPrefix: true, persona: 'professional_accountant' },
  { id: 'professional-calendar', icon: 'calendarDays', label: 'Agenda', href: '/professional/calendar', persona: 'professional_accountant' },
  { id: 'professional-communications', icon: 'chatBubbleLeftRight', label: 'Communicatie', href: '/professional/communications', matchPrefix: true, persona: 'professional_accountant' },
];

const intermediaryNavItems: NavItem[] = [
  { id: 'advisor-dashboard', icon: 'chartBar', label: 'Dashboard', href: '/advisor', persona: 'intermediary_advisor' },
  { id: 'advisor-clients', icon: 'users', label: 'Cliënten', href: '/advisor/clients', matchPrefix: true, persona: 'intermediary_advisor' },
  { id: 'advisor-tasks', icon: 'clipboardDocumentList', label: 'Taken', href: '/advisor/tasks', matchPrefix: true, persona: 'intermediary_advisor' },
  { id: 'advisor-calendar', icon: 'calendarDays', label: 'Agenda', href: '/advisor/calendar', persona: 'intermediary_advisor' },
  { id: 'advisor-renewals', icon: 'arrowPath', label: 'Verlengingen', href: '/advisor/renewals', persona: 'intermediary_advisor' },
  { id: 'advisor-gap-analysis', icon: 'magnifyingGlassChart', label: 'Gap Analyses', href: '/advisor/gap-analysis', matchPrefix: true, persona: 'intermediary_advisor' },
  { id: 'advisor-commissions', icon: 'currencyEuro', label: 'Provisie', href: '/advisor/commissions', persona: 'intermediary_advisor' },
];

const adminNavItems: NavItem[] = [
  { id: 'admin', icon: 'shield', label: 'Beheer', href: '/admin', matchPrefix: true, persona: 'tenant_admin' },
];

interface SidebarProps {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  currentPath: string;
  hasPersona: (persona: UserPersona) => boolean;
}

export function Sidebar({ collapsed, onCollapsedChange, currentPath, hasPersona }: SidebarProps) {
  const isActive = (item: NavItem) => {
    if (item.matchPrefix) {
      return currentPath.startsWith(item.href);
    }
    return currentPath === item.href;
  };

  const filterByPersona = (items: NavItem[]) =>
    items.filter((item) => !item.persona || hasPersona(item.persona));

  const filteredConsumer = filterByPersona(consumerNavItems);
  const filteredDelegate = filterByPersona(delegateNavItems);
  const filteredProfessional = filterByPersona(professionalNavItems);
  const filteredIntermediary = filterByPersona(intermediaryNavItems);
  const filteredAdmin = filterByPersona(adminNavItems);

  const hasExtraNav = filteredConsumer.length > 0 || filteredDelegate.length > 0 || filteredProfessional.length > 0 || filteredIntermediary.length > 0;

  return (
    <aside
      id="main-nav"
      aria-label="Hoofdnavigatie"
      className={cn(
        // GAP-003: Show navigation on tablet (md) and desktop (lg)
        // Hidden on mobile (<768px), visible from md breakpoint
        'fixed left-0 top-16 z-40 hidden h-[calc(100vh-4rem)] flex-col border-r border-gray-200 bg-white transition-all duration-200 md:flex',
        // Tablet (md to lg): Always collapsed as navigation rail
        // Desktop (lg+): Respect collapsed state
        'md:w-16 lg:w-64',
        collapsed && 'lg:w-16'
      )}
    >
      <nav className="flex-1 overflow-y-auto py-4">
        {/* Main nav */}
        <div className="space-y-1 px-3">
          {mainNavItems.map((item) => (
            <NavLink key={item.id} item={item} active={isActive(item)} collapsed={collapsed} />
          ))}
        </div>

        {/* Consumer/Delegate/Professional/Intermediary nav */}
        {hasExtraNav && (
          <>
            <div className="my-4 border-t border-gray-100" />
            <div className="space-y-1 px-3">
              {[...filteredConsumer, ...filteredDelegate, ...filteredProfessional, ...filteredIntermediary].map((item) => (
                <NavLink key={item.id} item={item} active={isActive(item)} collapsed={collapsed} />
              ))}
            </div>
          </>
        )}

        {/* Admin nav */}
        {filteredAdmin.length > 0 && (
          <>
            <div className="my-4 border-t border-gray-100" />
            <div className="space-y-1 px-3">
              {filteredAdmin.map((item) => (
                <NavLink key={item.id} item={item} active={isActive(item)} collapsed={collapsed} />
              ))}
            </div>
          </>
        )}
      </nav>

      {/* Sidebar footer */}
      <div className="border-t border-gray-100 p-3">
        {/* Storage indicator - hidden on tablet (md), shown on desktop (lg) when not collapsed */}
        {!collapsed && (
          <div className="mb-3 hidden rounded-lg bg-gray-50 p-3 lg:block">
            <div className="mb-2 flex items-center justify-between text-xs text-gray-600">
              <span>Opslag</span>
              <span>2.4 / 5 GB</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-gray-200">
              <div className="h-full w-[48%] rounded-full bg-blue-600" />
            </div>
            <Link
              to="/subscription"
              className="mt-2 block text-xs text-blue-600 hover:underline"
            >
              Upgrade voor meer
            </Link>
          </div>
        )}

        {/* Collapse button - only on desktop (lg), tablet always shows collapsed rail */}
        <button
          onClick={() => onCollapsedChange(!collapsed)}
          className="hidden w-full items-center justify-center rounded-lg p-2 text-gray-500 hover:bg-gray-100 lg:flex"
          aria-label={collapsed ? 'Zijbalk uitklappen' : 'Zijbalk inklappen'}
        >
          <Icon
            name={collapsed ? 'chevronRight' : 'chevronLeft'}
            variant="outline"
            size="sm"
          />
          {!collapsed && <span className="ml-2 text-sm">Inklappen</span>}
        </button>
      </div>
    </aside>
  );
}

/**
 * NavLink - Sidebar navigation item
 *
 * GAP-002: Hub-and-spoke navigation improvements:
 * - Left border highlight on active items
 * - Tooltip on collapsed state
 * - Touch-friendly sizing
 */
function NavLink({
  item,
  active,
  collapsed,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
}) {
  return (
    <Link
      to={item.href}
      className={cn(
        'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        // GAP-002: Left border highlight on active items
        active
          ? 'border-l-4 border-l-mv-blue-600 bg-mv-blue-100 text-mv-blue-900 pl-2'
          : 'border-l-4 border-l-transparent text-gray-700 hover:bg-gray-100 hover:border-l-mv-gray-300',
        // GAP-006: Touch targets
        'min-h-[44px] md:min-h-[40px]',
        collapsed && 'justify-center px-2'
      )}
      aria-current={active ? 'page' : undefined}
    >
      <Icon name={item.icon} variant={active ? 'solid' : 'outline'} size="md" />
      {/* Desktop expanded: show label */}
      {!collapsed && (
        <>
          <span className="flex-1">{item.label}</span>
          {item.badge !== undefined && item.badge > 0 && (
            <span className="rounded-full bg-mv-blue-600 px-2 py-0.5 text-xs text-white">
              {item.badge}
            </span>
          )}
        </>
      )}
      {/* GAP-002: Tooltip for collapsed state */}
      {collapsed && (
        <span
          className={cn(
            'pointer-events-none absolute left-full ml-2 whitespace-nowrap rounded-md bg-mv-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity',
            'group-hover:opacity-100 group-focus-visible:opacity-100',
            'z-50'
          )}
          role="tooltip"
        >
          {item.label}
          {item.badge !== undefined && item.badge > 0 && (
            <span className="ml-1 rounded-full bg-mv-blue-500 px-1.5 py-0.5">
              {item.badge}
            </span>
          )}
        </span>
      )}
    </Link>
  );
}
